/**
 *  Server-Sent Events (SSE) API
 * @param {*} url 服务地址
 * @param {*} options 配置项
 * @returns
 */
import router from '@/router';
import store from '@/store';
import { ElMessageBox } from 'element-plus';
import { getStorageAuthInfo } from '@/utils/storageOperateApi';
import { LOCAL_STORAGE_KEY } from '@/constants';
import { trackGlobalAlert, ALERT_CHANNEL, ALERT_SCENE } from '@/utils/globalAlertTrack';
import i18n from '@/plugins/i18n';
const SSE = function (url, options) {
  if (!(this instanceof SSE)) {
    return new SSE(url, options);
  }

  this.INITIALIZING = -1;
  this.CONNECTING = 0;
  this.OPEN = 1;
  this.CLOSED = 2;

  this.url = url;

  options = options || {};
  this.headers = options.headers || {};
  this.payload = options.payload !== undefined ? options.payload : '';
  this.method = options.method || (this.payload && 'POST') || 'GET';
  this.withCredentials = !!options.withCredentials;

  this.FIELD_SEPARATOR = ':';
  this.listeners = {};

  this.xhr = null;
  this.readyState = this.INITIALIZING;
  this.progress = 0;
  this.chunk = '';

  this.addEventListener = function (type, listener) {
    if (this.listeners[type] === undefined) {
      this.listeners[type] = [];
    }

    if (this.listeners[type].indexOf(listener) === -1) {
      this.listeners[type].push(listener);
    }
  };

  this.removeEventListener = function (type, listener) {
    if (this.listeners[type] === undefined) {
      return;
    }

    const filtered = [];
    this.listeners[type].forEach(function (element) {
      if (element !== listener) {
        filtered.push(element);
      }
    });
    if (filtered.length === 0) {
      delete this.listeners[type];
    } else {
      this.listeners[type] = filtered;
    }
  };

  this.dispatchEvent = function (e) {
    if (!e) {
      return true;
    }

    e.source = this;

    const onHandler = 'on' + e.type;
    if (this.hasOwnProperty(onHandler)) {
      this[onHandler].call(this, e);
      if (e.defaultPrevented) {
        return false;
      }
    }

    if (this.listeners[e.type]) {
      return this.listeners[e.type].every(function (callback) {
        callback(e);
        return !e.defaultPrevented;
      });
    }

    return true;
  };

  this._setReadyState = function (state) {
    const event = new CustomEvent('readystatechange');
    event.readyState = state;
    this.readyState = state;
    this.dispatchEvent(event);
  };

  this._onStreamFailure = function (e) {
    const event = new CustomEvent('error');
    event.data = e.currentTarget.response;
    this.dispatchEvent(event);
    this.close();
  };

  this._onStreamAbort = function () {
    this.dispatchEvent(new CustomEvent('abort'));
    this.close();
  };

  this._onStreamProgress = function (e) {
    if (!this.xhr) {
      return;
    }

    if (this.xhr.status !== 200) {
      this._onStreamFailure(e);
      return;
    }

    if (this.readyState == this.CONNECTING) {
      this.dispatchEvent(new CustomEvent('open'));
      this._setReadyState(this.OPEN);
    }

    const data = this.xhr.responseText.substring(this.progress);
    this.progress += data.length;
    data.split(/(\r\n|\r|\n){2}/g).forEach(
      function (part) {
        if (part.trim().length === 0) {
          this.dispatchEvent(this._parseEventChunk(this.chunk.trim()));
          this.chunk = '';
        } else {
          this.chunk += part;
        }
      }.bind(this)
    );
  };

  this._onStreamLoaded = function (e) {
    this._onStreamProgress(e);

    // Parse the last chunk.
    this.dispatchEvent(this._parseEventChunk(this.chunk));
    this.chunk = '';
  };

  /**
   * 将接收到的SSE事件块解析为构造的事件对象。
   */
  this._parseEventChunk = function (chunk) {
    if (!chunk || chunk.length === 0) {
      return null;
    }

    const e = { id: null, retry: null, data: '', event: 'message' };
    chunk.split(/\n|\r\n|\r/).forEach(
      function (line) {
        line = line.trimRight();
        const index = line.indexOf(this.FIELD_SEPARATOR);
        if (index <= 0) {
          // Line was either empty, or started with a separator and is a comment.
          // Either way, ignore.
          return;
        }

        const field = line.substring(0, index);
        if (!(field in e)) {
          return;
        }

        const value = line.substring(index + 1).trimLeft();
        if (field === 'data') {
          e[field] += value;
        } else {
          e[field] = value;
        }
      }.bind(this)
    );

    const event = new CustomEvent(e.event);
    event.data = e.data;
    event.id = e.id;
    return event;
  };

  this._checkStreamClosed = function (e) {
    if (!this.xhr) {
      return;
    }
    // 登录超时
    if (!e.target?.responseText.startsWith('data:{')) {
      const response = JSON.parse(e?.target?.responseText || '{}');
      if (e.target.status === 200 && response?.code == 201) {
        // 与请求链路的登录失效框同源，同样上报便于线上还原触发原因
        trackGlobalAlert({
          channel: ALERT_CHANNEL.SSE,
          scene: ALERT_SCENE.SSE_STREAM,
          code: response.code,
          message: response?.msg || response.errordesc,
          config: { url: this.url, headers: this.headers }
        });
        ElMessageBox.alert(response?.msg || response.errordesc, i18n.global.t('common.tip.tips'), {
          customClass: 'message-box-normal',
          confirmButtonText: '前往登录',
          callback: async () => {
            // 退出登录以及清空本地缓存
            await store.dispatch('logoutAction');
            router.push(`/login?redirect_uri=${encodeURIComponent(window.location.pathname + window.location.search)}`);
          }
        });
        this.close();
        return;
      }
      if (e.target.status === 200 && response?.code == 500) {
        ElMessageBox.alert(response?.msg || response.errordesc, i18n.global.t('common.tip.tips'), {
          customClass: 'message-box-normal',
          confirmButtonText: i18n.global.t('common.button.confirm'),
          callback: async () => {}
        });
        this.close();
        return;
      }
    }

    if (this.xhr.readyState === XMLHttpRequest.DONE) {
      this._setReadyState(this.CLOSED);
    }
  };

  // 出发事件
  this.stream = function () {
    this._setReadyState(this.CONNECTING);

    this.xhr = new XMLHttpRequest();
    this.xhr.addEventListener('progress', this._onStreamProgress.bind(this));
    this.xhr.addEventListener('load', this._onStreamLoaded.bind(this));
    this.xhr.addEventListener('readystatechange', this._checkStreamClosed.bind(this));
    this.xhr.addEventListener('error', this._onStreamFailure.bind(this));
    this.xhr.addEventListener('abort', this._onStreamAbort.bind(this));
    this.xhr.open(this.method, this.url);
    for (const header in this.headers) {
      this.xhr.setRequestHeader(header, this.headers[header]);
    }
    this.xhr.withCredentials = this.withCredentials;
    this.xhr.send(this.payload);
  };

  this.close = function () {
    console.warn('[sse] front close', new Error().stack);

    if (this.readyState === this.CLOSED) {
      return;
    }

    this.xhr.abort();
    this.xhr = null;
    this._setReadyState(this.CLOSED);
  };
};

export const post = function (url, payload, options) {
  const jmUserInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO);
  options = options || {};
  options = {
    ...options,
    headers: {
      // 调用方业务头（如研究模式敏感词标记 c / b / brandChannel）先合并进来
      ...(options.headers || {}),
      // token / uid 始终以本地登录态为准，避免被调用方空值覆盖导致鉴权失败
      token: jmUserInfo?.webtoken || jmUserInfo.token || '',
      uid: jmUserInfo?.uid || ''
    }
  };
  return new SSE(url, {
    method: 'POST',
    payload: JSON.stringify(payload),
    ...options
  });
};

export default SSE;
